import { z } from "zod";
import {
  dataKeySchema,
  integrationRecordIdSchema,
  integrationDataQuerySchema,
  integrationDataValuesSchema,
  parseProject,
  type Context,
  type Project,
  type IntegrationDataQuery,
  type IntegrationDataValues,
} from "@proof/core";
export const formDataBindingsSchema = z
  .array(
    z
      .object({
        fieldId: z.string().min(1).max(200),
        sourceId: dataKeySchema,
        recordId: integrationRecordIdSchema,
        sourceField: dataKeySchema,
      })
      .strict(),
  )
  .max(100)
  .refine(
    (bindings) =>
      new Set(bindings.map((binding) => binding.fieldId)).size ===
      bindings.length,
  );
export type FormDataBinding = z.infer<typeof formDataBindingsSchema>[number];
export type FormDataProvider = (
  request: IntegrationDataQuery,
  signal: AbortSignal,
) => Promise<{
  version: 1;
  sourceId: string;
  recordId: string;
  values: IntegrationDataValues;
}>;
export function formDataRequests(
  bindings: FormDataBinding[],
  parameters: Record<string, Record<string, unknown>> = {},
) {
  const groups = new Map<string, IntegrationDataQuery>();
  for (const binding of bindings) {
    const key = JSON.stringify([binding.sourceId, binding.recordId]);
    const group =
      groups.get(key) ||
      integrationDataQuerySchema.parse({
        sourceId: binding.sourceId,
        recordId: binding.recordId,
        fields: [binding.sourceField],
        parameters: parameters[binding.sourceId] || {},
      });
    if (!group.fields.includes(binding.sourceField))
      group.fields.push(binding.sourceField);
    groups.set(key, integrationDataQuerySchema.parse(group));
  }
  if (groups.size > 20)
    throw Error("A form may request at most 20 source/record pairs");
  return [...groups.values()];
}
export function applyFormData(
  project: Project,
  bindings: FormDataBinding[],
  responses: Awaited<ReturnType<FormDataProvider>>[],
): { project: Project; context: Context } {
  const copy = structuredClone(project),
    dynamic: Record<string, unknown> = Object.create(null);
  for (const [index, binding] of bindings.entries()) {
    const field = copy.fields.find((field) => field.id === binding.fieldId);
    if (!field) throw Error("Unknown form field binding");
    const response = responses.find(
      (response) =>
        response.sourceId === binding.sourceId &&
        response.recordId === binding.recordId,
    );
    if (
      !response ||
      response.version !== 1 ||
      !Object.hasOwn(response.values, binding.sourceField)
    )
      throw Error("Missing form data");
    const value = integrationDataValuesSchema.parse(response.values)[
      binding.sourceField
    ];
    const catalog =
      !!value && typeof value === "object" && !Array.isArray(value);
    const key = `integration_field_${index}`;
    field.source = {
      kind: "dynamic",
      key,
      dynamicMode: catalog ? "choices" : "value",
      ...(field.source.dynamicSourceId
        ? { dynamicSourceId: field.source.dynamicSourceId }
        : {}),
    };
    for (const part of copy.parts)
      if (part.overrides[field.id]?.source)
        part.overrides[field.id].source = structuredClone(field.source);
    dynamic[key] = value;
  }
  return {
    project: parseProject(copy),
    context: { dynamic: dynamic as IntegrationDataValues },
  };
}
